import { useState } from 'react';
import { GranularityToggle } from '../../accounts/GranularityToggle';
import { CompactSegmentedToggle } from '../../billing/overview/CompactSegmentedToggle';
import { ChartLegend } from '../../billing/overview/OverviewCharts';
import { OverviewCard } from '../../billing/overview/OverviewCard';
import {
    ACTIVITY_CHART_HEIGHT_PX,
    ACTIVITY_COLORS,
    ACTIVITY_SERIES_LABELS,
    ActivityChart
} from './ActivityChart';
import type { HistoryView, ReportingPeriod } from './overviewPeriod';
import { ACTIVITY_SCOPE_OPTIONS, MEASURE_OPTIONS, type ActivityScope } from './overviewSeries';
import { HistoryFallback } from './OverviewStates';
import type { PerPersonMeasure } from './perPersonRows';
import { useStoredChoice } from './useStoredChoice';

const MEASURE_STORAGE_KEY = 'monitoring_overview_activity_measure';

function legendItems(measure: PerPersonMeasure) {
    const labels = ACTIVITY_SERIES_LABELS[measure];
    return [
        { label: labels.raised, color: ACTIVITY_COLORS.raised },
        { label: labels.resolved, color: ACTIVITY_COLORS.resolved }
    ];
}

const LEGEND_ITEMS: Readonly<Record<PerPersonMeasure, { label: string; color: string }[]>> = {
    events: legendItems('events'),
    patients: legendItems('patients')
};

function activityCopy(measure: PerPersonMeasure, period: ReportingPeriod) {
    if (measure === 'events') {
        return {
            subtitle: period.label,
            info: 'What DM raised against what got resolved, however it was resolved'
        };
    }
    return {
        subtitle: `Patients · ${period.label.toLowerCase()}`,
        info: 'Patients DM raised something for against patients who had something resolved; each patient counts once per bar'
    };
}

interface ActivityTogglesProps {
    scope: ActivityScope;
    onScopeChange: (next: ActivityScope) => void;
    measure: PerPersonMeasure;
    onMeasureChange: (next: PerPersonMeasure) => void;
}

function ActivityToggles({ scope, onScopeChange, measure, onMeasureChange }: ActivityTogglesProps) {
    return (
        <div className="flex flex-wrap items-center justify-end gap-2">
            <GranularityToggle
                value={measure}
                options={MEASURE_OPTIONS}
                onChange={onMeasureChange}
                ariaLabel="Activity: count items or patients"
            />
            <CompactSegmentedToggle
                options={ACTIVITY_SCOPE_OPTIONS}
                active={scope}
                onChange={onScopeChange}
                ariaLabel="Activity: notifications, messages or both"
            />
        </div>
    );
}

export function ActivityCard({ history }: { history: HistoryView }) {
    const [scope, setScope] = useState<ActivityScope>('both');
    const [measure, setMeasure] = useStoredChoice(MEASURE_STORAGE_KEY, MEASURE_OPTIONS);
    const { data } = history;
    const copy = activityCopy(measure, history.period);
    return (
        <OverviewCard
            title="Activity over time"
            subtitle={copy.subtitle}
            info={copy.info}
            wrapAction
            action={
                <ActivityToggles
                    scope={scope}
                    onScopeChange={setScope}
                    measure={measure}
                    onMeasureChange={setMeasure}
                />
            }
        >
            {data ? (
                <div className="flex flex-col gap-3">
                    <ActivityChart
                        series={data.series}
                        bucket={data.bucket}
                        scope={scope}
                        measure={measure}
                    />
                    <ChartLegend items={LEGEND_ITEMS[measure]} />
                </div>
            ) : (
                <div style={{ minHeight: ACTIVITY_CHART_HEIGHT_PX }}>
                    <HistoryFallback failed={history.failed} />
                </div>
            )}
        </OverviewCard>
    );
}
